import * as React from "react";
import { useDispatch } from "redux-react-hook";
import { useHistory } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Table } from "src/core/dashboard/components/widgets";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import { PageProps } from "src/pages/Router/routes";
import { useDrop } from "src/core/drops/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { Button } from "src/library/components/Button";
import {
  BranchWastageGrouped,
  BranchWastageGroupedPagination,
  useWasteApi
} from "src/modules/waste/api/waste.api";
import { branchWastageGroupedTableColumns } from "src/modules/waste/components/tables/BranchWastageGroupedTableColumns";
import { BranchWastageGroupedActionList } from "src/modules/waste/components/tables/BranchWastageGroupedActionList";

const defaultPagination: BranchWastageGroupedPagination = {
  page: 0,
  limit: 10,
  offset: 0,
  sort: [ [ "wasteDate", "ASC" ] ]
};

const WasteOverviewPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ { drop } ] = useDrop();
  const [ getBranchWastageGroupedResponse, getBranchWastageGroupedRequest ] = useWasteApi("GET_BRANCH_WASTAGE_GROUPED");
  const history = useHistory();

  const wasteData = React.useMemo(() => {
    return getBranchWastageGroupedResponse.data?.result || [];
  }, [ getBranchWastageGroupedResponse.data ]);

  const isLoading = React.useMemo(() => {
    return getBranchWastageGroupedResponse.pending;
  }, [ getBranchWastageGroupedResponse.pending ]);

  const checkUserPermission = useCheckUserPermission();

  const onFetchData = React.useCallback((params: {filters: FilterParams<Pick<BranchWastageGrouped, "wasteDate">>, pagination: PaginatedParams<BranchWastageGrouped>}) => {
    if (drop?.id) {
      const queryParams = {
        branchId: drop.id,
        pagination: params.pagination,
        filters: params.filters
      };

      getBranchWastageGroupedRequest(queryParams);
    }
  }, [ drop?.id, getBranchWastageGroupedRequest ]);

  const tableColumns = React.useMemo(() => {
    return branchWastageGroupedTableColumns({ actionButton: info => <BranchWastageGroupedActionList waste={info.row.original} /> });
  }, []);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={checkUserPermission("create-list") && (
          <>
            <LikeToDo
              copy="What would you like to do?"
              buttons={[
                <Button
                  key="createWaste"
                  onClick={() => history.push("/waste/report-wastage")}
                >
                  Report Waste
                </Button>
              ]}
            />
          </>
        )}
      >
        {drop && (
          <Grid.Bounds direction="vertical">
            <Grid.Box>
              <Table
                error={getBranchWastageGroupedResponse.error}
                tableOf="Waste Overview"
                className="large"
                data={wasteData}
                columns={tableColumns}
                onFetchData={onFetchData}
                defaultPagination={defaultPagination}
                loading={isLoading}
                pageCount={getBranchWastageGroupedResponse.data?.pages || 0}
                rowCount={getBranchWastageGroupedResponse.data?.count || 0}
              />
            </Grid.Box>
          </Grid.Bounds>
        )}
      </MainContentWrapper>
    </Layout>
  );
};

export default WasteOverviewPage;
